'use client';

import { useEffect } from 'react';

import { toast } from '@/libs/toast';
import {
  attemptChunkRecovery,
  isChunkLoadError,
  isNextStaticResourceError,
} from '@/utils/chunkError';
import { logger } from '@/utils/logger';

const STALE_SERVER_ACTION_PATTERN = /was not found on the server/i;

function notifyNewVersion() {
  toast.warning('Nova versão disponível', {
    description: 'Recarregue a página para continuar.',
    action: {
      label: 'Recarregar',
      onClick: () => globalThis.location.reload(),
    },
    duration: 8000,
  });
}

// Attach global error listeners on the client to capture route + stack
export function GlobalErrorListeners() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onError = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: unknown,
    ) => {
      try {
        const meta = {
          channel: 'global-error',
          url: globalThis.location.href,
          message: typeof message === 'string' ? message : undefined,
          source,
          lineno,
          colno,
          // Best-effort stack if available
          stack:
            error && typeof error === 'object' && 'stack' in error
              ? (error as { stack?: string }).stack
              : undefined,
        } as const;

        if (isChunkLoadError(error ?? message)) {
          logger.error('[window.onerror] Chunk load error', meta);
          if (!attemptChunkRecovery('window.onerror')) {
            notifyNewVersion();
          }
          return false;
        }

        logger.error('[window.onerror] Unhandled error', meta);
        // Forward directly to Datadog as well for redundancy
        // eslint-disable-next-line @typescript-eslint/no-var-requires,@typescript-eslint/no-require-imports
        const { datadogLogs } = require('@datadog/browser-logs');
        datadogLogs.logger.error(
          '[window.onerror] Unhandled error',
          meta as unknown as Record<string, unknown>,
        );
      } catch {
        /* noop */
      }
      // Do not prevent default
      return false;
    };

    const onResourceError = (event: Event) => {
      try {
        if (!isNextStaticResourceError(event)) return;

        logger.error('[resource-error] Failed to load static asset', {
          channel: 'chunk-error',
          url: globalThis.location.href,
          asset:
            event.target instanceof HTMLScriptElement
              ? event.target.src
              : (event.target as HTMLLinkElement).href,
        });

        if (!attemptChunkRecovery('resource-error')) {
          notifyNewVersion();
        }
      } catch {
        /* noop */
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason as unknown;
        const message =
          reason && typeof reason === 'object' && 'message' in reason
            ? String((reason as { message?: unknown }).message)
            : typeof reason === 'string'
              ? reason
              : '';

        if (STALE_SERVER_ACTION_PATTERN.test(message)) {
          event.preventDefault();
          notifyNewVersion();
          return;
        }

        if (isChunkLoadError(reason)) {
          event.preventDefault();
          logger.error('[unhandledrejection] Chunk load error', {
            channel: 'chunk-error',
            url: globalThis.location.href,
            message,
          });
          if (!attemptChunkRecovery('unhandledrejection')) {
            notifyNewVersion();
          }
          return;
        }

        const meta = {
          channel: 'global-error',
          url: globalThis.location.href,
          type: 'unhandledrejection',
          reason,
          stack:
            reason && typeof reason === 'object' && 'stack' in reason
              ? (reason as { stack?: string }).stack
              : undefined,
        } as const;
        logger.error('[unhandledrejection] Unhandled promise rejection', meta);
        // eslint-disable-next-line @typescript-eslint/no-var-requires,@typescript-eslint/no-require-imports
        const { datadogLogs } = require('@datadog/browser-logs');
        datadogLogs.logger.error(
          '[unhandledrejection] Unhandled promise rejection',
          meta as unknown as Record<string, unknown>,
        );
      } catch {
        /* noop */
      }
    };

    globalThis.addEventListener('error', onError as unknown as EventListener);
    globalThis.addEventListener('error', onResourceError, true);
    globalThis.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      globalThis.removeEventListener('error', onError as unknown as EventListener);
      globalThis.removeEventListener('error', onResourceError, true);
      globalThis.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return null;
}
